import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";
import { Spinner } from "flowbite-react";
import {
  HiCalendar,
  HiClock,
  HiUser,
  HiOfficeBuilding,
  HiCheckCircle,
  HiXCircle,
  HiClock as HiPending,
  HiArrowLeft,
  HiVideoCamera,
  HiCash,
  HiShoppingCart,
  HiCreditCard,
  HiClipboardList,
  HiDocumentText,
  HiPencil,
} from "react-icons/hi";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { getLoggedInUser } from "../utils/authUser";

const SECTION_TABS = {
  doctor: "Doctor Appointments",
  nurse: "Nurse Appointments",
  payments: "Payment History",
  orders: "Orders",
  prescriptions: "Prescriptions",
};

const AppointmentHistory = () => {
  const navigate = useNavigate();
  const [activeSection, setActiveSection] = useState("doctor");
  const [doctorAppointments, setDoctorAppointments] = useState([]);
  const [nurseAppointments, setNurseAppointments] = useState([]);
  const [payments, setPayments] = useState([]);
  const [orders, setOrders] = useState([]);
  const [prescriptions, setPrescriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    const fetchHistory = async () => {
      const customer = getLoggedInUser();

      if (!customer || !customer.id) {
        toast.error("Please login to view history");
        navigate("/login");
        return;
      }

      setLoading(true);
      try {
        const response = await axios.get(
          `https://app.caaficare.so/api/appointments/history?customer_id=${customer.id}`
        );

        if (response.data.success) {
          const data = response.data.data || {};
          setDoctorAppointments(
            (data.doctorAppointments || []).map((a) => ({
              ...a,
              type: "doctor",
              video_url: a.video_url || a.videoUrl || a.meeting_url || null,
            }))
          );
          setNurseAppointments(
            (data.nurseAppointments || []).map((a) => ({
              ...a,
              type: "nurse",
              video_url: a.video_url || a.videoUrl || a.meeting_url || null,
            }))
          );
          setPayments(data.payments || []);
          setOrders(data.orders || []);
          setPrescriptions(data.prescriptions || []);
        } else {
          toast.error("Failed to load history");
        }
      } catch (error) {
        console.error("Error fetching history:", error);
        toast.error("Failed to load history");
      } finally {
        setLoading(false);
      }
    };

    fetchHistory();
  }, [navigate]);

  const getStatusBadge = (status) => {
    const statusLower = status?.toLowerCase() || "pending";

    switch (statusLower) {
      case "approved":
      case "completed":
      case "paid":
        return (
          <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-bold bg-green-100 text-green-700">
            <HiCheckCircle className="w-4 h-4" />
            {statusLower === "paid" ? "Paid" : "Completed"}
          </span>
        );
      case "canceled":
      case "cancelled":
        return (
          <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-bold bg-red-100 text-red-700">
            <HiXCircle className="w-4 h-4" />
            Canceled
          </span>
        );
      case "scheduled":
        return (
          <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-bold bg-blue-100 text-blue-700">
            <HiPending className="w-4 h-4" />
            Scheduled
          </span>
        );
      case "confirmed":
        return (
          <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-bold bg-indigo-100 text-indigo-700">
            <HiCheckCircle className="w-4 h-4" />
            Confirmed
          </span>
        );
      case "pending":
        return (
          <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-bold bg-yellow-100 text-yellow-700">
            <HiPending className="w-4 h-4" />
            Pending
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-bold bg-gray-100 text-gray-700">
            <HiPending className="w-4 h-4" />
            {status || "Pending"}
          </span>
        );
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = dateString instanceof Date ? dateString : new Date(dateString);
    if (isNaN(date.getTime())) return "Invalid Date";
    return date.toLocaleDateString("en-US", {
      weekday: "short",
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const formatTime = (timeString) => {
    if (!timeString) return "N/A";
    if (timeString.includes("AM") || timeString.includes("PM")) return timeString;
    if (timeString.includes(":")) {
      const [hours, minutes] = timeString.split(":");
      const hour = parseInt(hours);
      if (isNaN(hour)) return timeString;
      const period = hour >= 12 ? "PM" : "AM";
      const displayHour = hour > 12 ? hour - 12 : hour === 0 ? 12 : hour;
      return `${displayHour}:${minutes} ${period}`;
    }
    return timeString;
  };

  const getAppointmentType = (appointment) => {
    if (appointment.doctor) {
      return {
        type: "Doctor",
        name: appointment.doctor.name || "Doctor",
        profession: appointment.doctor.profession,
        hospitalName: appointment.doctor.hospital?.name || null,
      };
    }
    if (appointment.nurse) {
      return {
        type: "Nurse",
        name: appointment.nurse.name,
        profession: "Nursing Care",
        hospitalName: null,
      };
    }
    return {
      type: "Service",
      name: appointment.service?.serviceName || "Service",
      profession: "Healthcare Service",
      hospitalName: null,
    };
  };

  const filterAppointments = (list) => {
    if (filter === "all") return list;
    const statusLower = (s) => (s || "").toLowerCase();
    return list.filter((apt) => {
      const s = statusLower(apt.status);
      if (filter === "pending") return s === "pending" || s === "scheduled";
      if (filter === "approved") return s === "approved" || s === "completed" || s === "confirmed";
      if (filter === "canceled") return s === "canceled" || s === "cancelled";
      return s === filter.toLowerCase();
    });
  };

  const filteredDoctor = filterAppointments(doctorAppointments);
  const filteredNurse = filterAppointments(nurseAppointments);

  const getStatusCount = (list, statusFilter) => {
    if (statusFilter === "all") return list.length;
    return list.filter((apt) => {
      const s = (apt.status || "").toLowerCase();
      if (statusFilter === "pending") return s === "pending" || s === "scheduled";
      if (statusFilter === "approved") return s === "approved" || s === "completed" || s === "confirmed";
      if (statusFilter === "canceled") return s === "canceled" || s === "cancelled";
      return s === statusFilter.toLowerCase();
    }).length;
  };

  const AppointmentCard = ({ appointment }) => {
    const info = getAppointmentType(appointment);
    const itemKey = `${appointment.type || "apt"}-${appointment.id}`;
    return (
      <div
        key={itemKey}
        className="bg-white rounded-2xl shadow-lg border-2 border-gray-100 p-6 hover:shadow-xl transition-all"
      >
        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
          <div className="flex-1">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h3 className="text-xl font-black text-gray-900 mb-1">
                  {info.type} Appointment
                </h3>
                <div className="flex items-center gap-2">
                  <p className="text-sm text-gray-500">{info.profession}</p>
                  {appointment.appointmentType && (
                    <>
                      <span className="text-gray-300">•</span>
                      <div className="flex items-center gap-1 text-xs text-blue-600 font-bold">
                        <HiVideoCamera className="w-3 h-3" />
                        {appointment.appointmentType}
                      </div>
                    </>
                  )}
                </div>
              </div>
              {getStatusBadge(appointment.status)}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center">
                  <HiUser className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-xs font-bold text-gray-400 uppercase mb-1">{info.type}</p>
                  <p className="text-sm font-bold text-gray-900">{info.name}</p>
                </div>
              </div>

              {info.hospitalName && (
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-green-50 flex items-center justify-center">
                    <HiOfficeBuilding className="w-5 h-5 text-green-600" />
                  </div>
                  <div>
                    <p className="text-xs font-bold text-gray-400 uppercase mb-1">Hospital</p>
                    <p className="text-sm font-bold text-gray-900">{info.hospitalName}</p>
                  </div>
                </div>
              )}

              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-purple-50 flex items-center justify-center">
                  <HiCalendar className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-xs font-bold text-gray-400 uppercase mb-1">Date</p>
                  <p className="text-sm font-bold text-gray-900">
                    {formatDate(appointment.appointmentDate)}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-orange-50 flex items-center justify-center">
                  <HiClock className="w-5 h-5 text-orange-600" />
                </div>
                <div>
                  <p className="text-xs font-bold text-gray-400 uppercase mb-1">Time</p>
                  <p className="text-sm font-bold text-gray-900">
                    {formatTime(appointment.appointmentTime)}
                  </p>
                </div>
              </div>
            </div>

            {appointment.patientName && (
              <div className="mt-4 pt-4 border-t border-gray-100">
                <p className="text-xs font-bold text-gray-400 uppercase mb-1">Patient Information</p>
                <div className="flex flex-wrap gap-4 text-sm">
                  <span className="text-gray-700">
                    <span className="font-bold">Name:</span> {appointment.patientName}
                  </span>
                  {appointment.patientAge && (
                    <span className="text-gray-700">
                      <span className="font-bold">Age:</span> {appointment.patientAge}
                    </span>
                  )}
                  {appointment.patientGender && (
                    <span className="text-gray-700">
                      <span className="font-bold">Gender:</span> {appointment.patientGender}
                    </span>
                  )}
                </div>
              </div>
            )}

            {appointment.notes && (
              <div className="mt-4 pt-4 border-t border-gray-100">
                <p className="text-xs font-bold text-gray-400 uppercase mb-2">Notes</p>
                <p className="text-sm text-gray-700">{appointment.notes}</p>
              </div>
            )}

            {(appointment.video_url || appointment.videoUrl) && (
              <div className="mt-4 pt-4 border-t border-gray-100">
                <p className="text-xs font-bold text-gray-400 uppercase mb-2">Video consultation</p>
                <a
                  href={appointment.video_url || appointment.videoUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-bold text-sm shadow-lg shadow-blue-200 transition-all"
                >
                  <HiVideoCamera className="w-5 h-5" />
                  Join video call
                </a>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header
          onAppointmentClick={() => navigate("/appointment")}
          onNurseAppointmentClick={() => navigate("/nurses-list")}
          onApplyNurseClick={() => {}}
          onApplyDoctorClick={() => {}}
        />
        <div className="pt-24 pb-16">
          <div className="max-w-7xl mx-auto px-6">
            <div className="flex items-center justify-center min-h-[60vh]">
              <div className="text-center">
                <Spinner size="xl" />
                <p className="mt-4 text-gray-500">Loading your history...</p>
              </div>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header
        onAppointmentClick={() => navigate("/appointment")}
        onNurseAppointmentClick={() => navigate("/nurses-list")}
        onApplyNurseClick={() => {}}
        onApplyDoctorClick={() => {}}
      />

      <div className="pt-24 pb-16">
        <div className="max-w-7xl mx-auto px-6">
          <div className="mb-8">
            <button
              onClick={() => navigate("/")}
              className="flex items-center gap-2 text-gray-600 hover:text-blue-600 mb-4 transition-colors"
            >
              <HiArrowLeft className="w-5 h-5" />
              <span className="text-sm font-bold">Back to Home</span>
            </button>
            <h1 className="text-4xl font-black text-gray-900 mb-2">My History</h1>
            <p className="text-gray-500">
              View your appointments, payments, orders, and prescriptions
            </p>
          </div>

          {/* Section Tabs */}
          <div className="flex flex-wrap gap-2 mb-6">
            {Object.entries(SECTION_TABS).map(([key, label]) => {
              const count =
                key === "doctor"
                  ? doctorAppointments.length
                  : key === "nurse"
                  ? nurseAppointments.length
                  : key === "payments"
                  ? payments.length
                  : key === "orders"
                  ? orders.length
                  : prescriptions.length;
              return (
                <button
                  key={key}
                  onClick={() => setActiveSection(key)}
                  className={`px-5 py-2.5 rounded-xl font-bold text-sm transition-all flex items-center gap-2 ${
                    activeSection === key
                      ? "bg-blue-600 text-white shadow-lg shadow-blue-200"
                      : "bg-white text-gray-700 border-2 border-gray-200 hover:border-blue-300"
                  }`}
                >
                  {key === "doctor" && <HiUser className="w-4 h-4" />}
                  {key === "nurse" && <HiClipboardList className="w-4 h-4" />}
                  {key === "payments" && <HiCreditCard className="w-4 h-4" />}
                  {key === "orders" && <HiShoppingCart className="w-4 h-4" />}
                  {key === "prescriptions" && <HiDocumentText className="w-4 h-4" />}
                  {label} ({count})
                </button>
              );
            })}
          </div>

          {/* Doctor Appointments Section */}
          {activeSection === "doctor" && (
            <>
              <div className="flex flex-wrap gap-3 mb-6">
                {["all", "pending", "approved", "canceled"].map((f) => (
                  <button
                    key={f}
                    onClick={() => setFilter(f)}
                    className={`px-6 py-2.5 rounded-xl font-bold text-sm transition-all ${
                      filter === f
                        ? f === "all"
                          ? "bg-blue-600 text-white shadow-lg shadow-blue-200"
                          : f === "pending"
                          ? "bg-yellow-500 text-white"
                          : f === "approved"
                          ? "bg-green-500 text-white"
                          : "bg-red-500 text-white"
                        : "bg-white text-gray-700 border-2 border-gray-200"
                    }`}
                  >
                    {f === "all" && `All (${doctorAppointments.length})`}
                    {f === "pending" && `Scheduled (${getStatusCount(doctorAppointments, "pending")})`}
                    {f === "approved" && `Completed (${getStatusCount(doctorAppointments, "approved")})`}
                    {f === "canceled" && `Canceled (${getStatusCount(doctorAppointments, "canceled")})`}
                  </button>
                ))}
              </div>

              {filteredDoctor.length === 0 ? (
                <div className="bg-white rounded-3xl shadow-xl p-12 text-center">
                  <div className="w-24 h-24 bg-blue-50 rounded-full flex items-center justify-center mx-auto mb-6">
                    <HiUser className="w-12 h-12 text-blue-400" />
                  </div>
                  <h3 className="text-2xl font-black text-gray-900 mb-2">No Doctor Appointments</h3>
                  <p className="text-gray-500 mb-6">
                    {filter === "all"
                      ? "You don't have any doctor appointments yet."
                      : `No ${filter} doctor appointments found.`}
                  </p>
                  <button
                    onClick={() => navigate("/appointment")}
                    className="px-6 py-3 bg-blue-600 text-white rounded-xl font-bold hover:bg-blue-700 transition-all"
                  >
                    Book Doctor Appointment
                  </button>
                </div>
              ) : (
                <div className="space-y-4">
                  {filteredDoctor.map((apt) => (
                    <AppointmentCard key={`doctor-${apt.id}`} appointment={apt} />
                  ))}
                </div>
              )}
            </>
          )}

          {/* Nurse Appointments Section */}
          {activeSection === "nurse" && (
            <>
              <div className="flex flex-wrap gap-3 mb-6">
                {["all", "pending", "approved", "canceled"].map((f) => (
                  <button
                    key={f}
                    onClick={() => setFilter(f)}
                    className={`px-6 py-2.5 rounded-xl font-bold text-sm transition-all ${
                      filter === f
                        ? f === "all"
                          ? "bg-blue-600 text-white"
                          : f === "pending"
                          ? "bg-yellow-500 text-white"
                          : f === "approved"
                          ? "bg-green-500 text-white"
                          : "bg-red-500 text-white"
                        : "bg-white text-gray-700 border-2 border-gray-200"
                    }`}
                  >
                    {f === "all" && `All (${nurseAppointments.length})`}
                    {f === "pending" && `Scheduled (${getStatusCount(nurseAppointments, "pending")})`}
                    {f === "approved" && `Completed (${getStatusCount(nurseAppointments, "approved")})`}
                    {f === "canceled" && `Canceled (${getStatusCount(nurseAppointments, "canceled")})`}
                  </button>
                ))}
              </div>

              {filteredNurse.length === 0 ? (
                <div className="bg-white rounded-3xl shadow-xl p-12 text-center">
                  <div className="w-24 h-24 bg-green-50 rounded-full flex items-center justify-center mx-auto mb-6">
                    <HiClipboardList className="w-12 h-12 text-green-400" />
                  </div>
                  <h3 className="text-2xl font-black text-gray-900 mb-2">No Nurse Appointments</h3>
                  <p className="text-gray-500 mb-6">
                    {filter === "all"
                      ? "You don't have any nurse appointments yet."
                      : `No ${filter} nurse appointments found.`}
                  </p>
                  <button
                    onClick={() => navigate("/nurses-list")}
                    className="px-6 py-3 bg-green-600 text-white rounded-xl font-bold hover:bg-green-700 transition-all"
                  >
                    Book Nurse Appointment
                  </button>
                </div>
              ) : (
                <div className="space-y-4">
                  {filteredNurse.map((apt) => (
                    <AppointmentCard key={`nurse-${apt.id}`} appointment={apt} />
                  ))}
                </div>
              )}
            </>
          )}

          {/* Payment History Section */}
          {activeSection === "payments" && (
            <>
              {payments.length === 0 ? (
                <div className="bg-white rounded-3xl shadow-xl p-12 text-center">
                  <div className="w-24 h-24 bg-indigo-50 rounded-full flex items-center justify-center mx-auto mb-6">
                    <HiCreditCard className="w-12 h-12 text-indigo-400" />
                  </div>
                  <h3 className="text-2xl font-black text-gray-900 mb-2">No Payment History</h3>
                  <p className="text-gray-500">You haven't made any payments yet.</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {payments.map((payment) => (
                    <div
                      key={payment.id}
                      className="bg-white rounded-2xl shadow-lg border-2 border-gray-100 p-6 hover:shadow-xl transition-all"
                    >
                      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-start justify-between mb-4">
                            <h3 className="text-xl font-black text-gray-900 flex items-center gap-2">
                              <HiCreditCard className="w-6 h-6 text-indigo-600" />
                              Payment #{payment.id}
                            </h3>
                            {getStatusBadge(payment.status)}
                          </div>

                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 rounded-xl bg-indigo-50 flex items-center justify-center">
                                <HiCash className="w-5 h-5 text-indigo-600" />
                              </div>
                              <div>
                                <p className="text-xs font-bold text-gray-400 uppercase mb-1">Amount</p>
                                <p className="text-lg font-bold text-gray-900">
                                  ${Number(payment.paid_amount).toFixed(2)}
                                </p>
                              </div>
                            </div>

                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 rounded-xl bg-purple-50 flex items-center justify-center">
                                <HiCreditCard className="w-5 h-5 text-purple-600" />
                              </div>
                              <div>
                                <p className="text-xs font-bold text-gray-400 uppercase mb-1">Payment Method</p>
                                <p className="text-sm font-bold text-gray-900">
                                  {payment.payment_method || "N/A"}
                                </p>
                              </div>
                            </div>

                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 rounded-xl bg-gray-50 flex items-center justify-center">
                                <HiCalendar className="w-5 h-5 text-gray-600" />
                              </div>
                              <div>
                                <p className="text-xs font-bold text-gray-400 uppercase mb-1">Date</p>
                                <p className="text-sm font-bold text-gray-900">
                                  {formatDate(payment.created_at)}
                                </p>
                              </div>
                            </div>

                            {payment.discount > 0 && (
                              <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-xl bg-amber-50 flex items-center justify-center">
                                  <HiCash className="w-5 h-5 text-amber-600" />
                                </div>
                                <div>
                                  <p className="text-xs font-bold text-gray-400 uppercase mb-1">Discount</p>
                                  <p className="text-sm font-bold text-gray-900">
                                    ${Number(payment.discount).toFixed(2)}
                                  </p>
                                </div>
                              </div>
                            )}
                          </div>

                          {(payment.appointment || payment.order || payment.nurse) && (
                            <div className="mt-4 pt-4 border-t border-gray-100">
                              <p className="text-xs font-bold text-gray-400 uppercase mb-2">Related to</p>
                              {payment.appointment && (
                                <p className="text-sm text-gray-700">
                                  Doctor Appointment: {payment.appointment.patientName || "N/A"} •{" "}
                                  {formatDate(payment.appointment.appointmentDate)} •{" "}
                                  {payment.appointment.doctor?.name || payment.appointment.nurse?.name || "—"}
                                </p>
                              )}
                              {!payment.appointment && payment.nurse && (
                                <p className="text-sm text-gray-700">
                                  Nurse Appointment with {payment.nurse.name}
                                </p>
                              )}
                              {payment.order && (
                                <p className="text-sm text-gray-700">
                                  Order #{payment.order.order_number} • Total: $
                                  {payment.order.totalPrice || "0"}
                                </p>
                              )}
                            </div>
                          )}

                          {payment.notes && (
                            <div className="mt-4 pt-4 border-t border-gray-100">
                              <p className="text-xs font-bold text-gray-400 uppercase mb-1">Notes</p>
                              <p className="text-sm text-gray-700">{payment.notes}</p>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}

          {/* Orders Section */}
          {activeSection === "orders" && (
            <>
              {orders.length === 0 ? (
                <div className="bg-white rounded-3xl shadow-xl p-12 text-center">
                  <div className="w-24 h-24 bg-orange-50 rounded-full flex items-center justify-center mx-auto mb-6">
                    <HiShoppingCart className="w-12 h-12 text-orange-400" />
                  </div>
                  <h3 className="text-2xl font-black text-gray-900 mb-2">No Orders</h3>
                  <p className="text-gray-500">You haven't placed any orders yet.</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {orders.map((order) => (
                    <div
                      key={order.id}
                      className="bg-white rounded-2xl shadow-lg border-2 border-gray-100 p-6 hover:shadow-xl transition-all"
                    >
                      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-start justify-between mb-4">
                            <h3 className="text-xl font-black text-gray-900 flex items-center gap-2">
                              <HiShoppingCart className="w-6 h-6 text-orange-600" />
                              Order #{order.order_number}
                            </h3>
                            {getStatusBadge(order.status)}
                          </div>

                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 rounded-xl bg-orange-50 flex items-center justify-center">
                                <HiCash className="w-5 h-5 text-orange-600" />
                              </div>
                              <div>
                                <p className="text-xs font-bold text-gray-400 uppercase mb-1">Total</p>
                                <p className="text-lg font-bold text-gray-900">
                                  ${order.totalPrice || "0"}
                                </p>
                              </div>
                            </div>

                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 rounded-xl bg-gray-50 flex items-center justify-center">
                                <HiCalendar className="w-5 h-5 text-gray-600" />
                              </div>
                              <div>
                                <p className="text-xs font-bold text-gray-400 uppercase mb-1">Date</p>
                                <p className="text-sm font-bold text-gray-900">
                                  {formatDate(order.created_at)}
                                </p>
                              </div>
                            </div>

                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center">
                                <HiUser className="w-5 h-5 text-blue-600" />
                              </div>
                              <div>
                                <p className="text-xs font-bold text-gray-400 uppercase mb-1">Type</p>
                                <p className="text-sm font-bold text-gray-900 capitalize">
                                  {order.type || "Pickup"}
                                </p>
                              </div>
                            </div>
                          </div>

                          {order.orderItems && order.orderItems.length > 0 && (
                            <div className="mt-4 pt-4 border-t border-gray-100">
                              <p className="text-xs font-bold text-gray-400 uppercase mb-2">Items</p>
                              <ul className="space-y-2">
                                {order.orderItems.map((item) => (
                                  <li
                                    key={item.id}
                                    className="flex justify-between text-sm text-gray-700"
                                  >
                                    <span>
                                      {item.product?.item_name || "Product"} × {item.quantity}
                                    </span>
                                    <span className="font-semibold">
                                      ${item.price || "0"}
                                    </span>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}

          {/* Prescriptions Section */}
          {activeSection === "prescriptions" && (
            <>
              {prescriptions.length === 0 ? (
                <div className="bg-white rounded-3xl shadow-xl p-12 text-center">
                  <div className="w-24 h-24 bg-teal-50 rounded-full flex items-center justify-center mx-auto mb-6">
                    <HiDocumentText className="w-12 h-12 text-teal-400" />
                  </div>
                  <h3 className="text-2xl font-black text-gray-900 mb-2">No Prescriptions</h3>
                  <p className="text-gray-500">You don't have any prescriptions yet.</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {prescriptions.map((rx) => (
                    <div
                      key={rx.id}
                      className="bg-white rounded-2xl shadow-lg border-2 border-gray-100 p-6 hover:shadow-xl transition-all"
                    >
                      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-start justify-between mb-4">
                            <h3 className="text-xl font-black text-gray-900 flex items-center gap-2">
                              <HiDocumentText className="w-6 h-6 text-teal-600" />
                              Prescription #{rx.id}
                            </h3>
                            {getStatusBadge(rx.status)}
                          </div>

                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 rounded-xl bg-teal-50 flex items-center justify-center">
                                <HiUser className="w-5 h-5 text-teal-600" />
                              </div>
                              <div>
                                <p className="text-xs font-bold text-gray-400 uppercase mb-1">Patient</p>
                                <p className="text-sm font-bold text-gray-900">{rx.patientName}</p>
                              </div>
                            </div>

                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center">
                                <HiPencil className="w-5 h-5 text-blue-600" />
                              </div>
                              <div>
                                <p className="text-xs font-bold text-gray-400 uppercase mb-1">Prescribed by</p>
                                <p className="text-sm font-bold text-gray-900">
                                  {rx.doctor?.name || "Doctor"}
                                </p>
                              </div>
                            </div>

                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 rounded-xl bg-gray-50 flex items-center justify-center">
                                <HiCalendar className="w-5 h-5 text-gray-600" />
                              </div>
                              <div>
                                <p className="text-xs font-bold text-gray-400 uppercase mb-1">Date</p>
                                <p className="text-sm font-bold text-gray-900">
                                  {formatDate(rx.date || rx.created_at)}
                                </p>
                              </div>
                            </div>

                            {rx.pharmacy && (
                              <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-xl bg-green-50 flex items-center justify-center">
                                  <HiOfficeBuilding className="w-5 h-5 text-green-600" />
                                </div>
                                <div>
                                  <p className="text-xs font-bold text-gray-400 uppercase mb-1">Pharmacy</p>
                                  <p className="text-sm font-bold text-gray-900">{rx.pharmacy.name}</p>
                                  {rx.pharmacy.location && (
                                    <p className="text-xs text-gray-500">{rx.pharmacy.location}</p>
                                  )}
                                </div>
                              </div>
                            )}
                          </div>

                          {rx.prescription && rx.prescription.length > 0 && (
                            <div className="mt-4 pt-4 border-t border-gray-100">
                              <p className="text-xs font-bold text-gray-400 uppercase mb-2">Medicines</p>
                              <ul className="space-y-3">
                                {rx.prescription.map((med) => (
                                  <li
                                    key={med.id}
                                    className="p-3 bg-gray-50 rounded-lg border border-gray-100"
                                  >
                                    <div className="font-semibold text-gray-900">{med.medicineName}</div>
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-1 text-sm text-gray-600 mt-1">
                                      {med.quantity && <span>Quantity: {med.quantity}</span>}
                                      {med.frequency && <span>Frequency: {med.frequency}</span>}
                                      {med.route && <span>Route: {med.route}</span>}
                                    </div>
                                    {med.instruction && (
                                      <p className="text-sm text-gray-600 mt-1">
                                        Instructions: {med.instruction}
                                      </p>
                                    )}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default AppointmentHistory;
